import React, { useState } from "react";
import { List, ListItem, ListItemText, pxToRem } from "@platform/style";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faExclamationCircle,
  faExclamationTriangle,
  faFileLines,
  faInfoCircle,
} from "@fortawesome/sharp-regular-svg-icons";
import { format, parseISO } from "date-fns";
import {
  ActivityRecord,
  ActivityType,
  getActivityParameterKey,
  getActivityParameterValue,
} from "../../models/ActivityRecord";
import { reportError } from "../../utils/errors";

library.add(faInfoCircle, faExclamationTriangle, faExclamationCircle, faFileLines);

interface WorkItemHistoryListProps {
  activityRecords: ActivityRecord[];
  listIndex: number;
}

function computeOccurredDateDetails(occurredDate: Date) {
  try {
    let today = new Date();
    let occurredDateISO = parseISO(occurredDate.toString());
    let formattedTodaysDate = format(new Date(today), "MM/dd/yyyy");
    var formattedOccurredDate = format(new Date(occurredDateISO), "MM/dd/yyyy");

    let dayAtStr = formattedOccurredDate + " at ";
    if (formattedTodaysDate === formattedOccurredDate) {
      dayAtStr = "Today at ";
      let timeDiff = (today.getTime() - occurredDateISO.getTime()) / 1000;
      let timeDiffInMin = Math.round(timeDiff / 60);
      if (timeDiffInMin < 60) {
        return timeDiffInMin + " minutes ago";
      }
    }

    let convertTime = require("convert-time");
    let twelveHourTime = convertTime(
      occurredDateISO.getHours() + ":" + occurredDateISO.getMinutes()
    );
    if (twelveHourTime) {
      let splitTime = twelveHourTime.split(":");
      if (splitTime[0] === "0") {
        return dayAtStr + "12:" + splitTime[1];
      }
      return dayAtStr + twelveHourTime;
    }
    return null;
  } catch (e) {
    reportError(e);
  }
  return null;
}

function getActivityDescription(record: ActivityRecord) {
  let desc;
  switch (record.Activity) {
    case ActivityType.CREATE:
      desc = "Created by";
      break;
    case ActivityType.CLAIM:
      desc = "Claimed by";
      break;
    case ActivityType.ACTIVATE:
      desc = "Activated by";
      break;
    case ActivityType.HOLD:
      return getActivityParameterKey(record) === "FollowupDate" ? (
        <>
          {"Put on hold by "}
          <b>{record.PerformedBy}</b>
          {" until "}
          {getActivityParameterValue(record)}
        </>
      ) : (
        <>
          {"Put on hold by "}
          <b>{record.PerformedBy}</b>
        </>
      );
    case ActivityType.ASSIGN:
      return (
        <>
          {"Assigned to "}
          <b>{getActivityParameterValue(record)}</b>
          {" by "}
          <b>{record.PerformedBy}</b>
        </>
      );
    case ActivityType.CANCEL:
      desc = "Cancelled by";
      break;
    case ActivityType.COMPLETE:
      desc = "Completed by";
      break;
    default:
      return null;
  }
  return (
    <>
      {desc} <b>{record.PerformedBy}</b>
    </>
  );
}

export const WorkItemHistoryList = (props: WorkItemHistoryListProps) => {
  const [expandedComments, setExpandedComments] = useState<{ [key: number]: boolean }>({});

  const toggleExpand = (index: number) => {
    setExpandedComments((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>, index: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleExpand(index);
    }
  };

  const listStyle = {
    height: pxToRem(200),
    overflow: "auto",
    paddingTop: pxToRem(0),
    paddingBottom: pxToRem(24),
    paddingRight: pxToRem(24),
  };

  const listItemStyle = {
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: "divider",
    paddingLeft: 0,
    lineHeight: pxToRem(21),
    fontSize: pxToRem(14),
  };

  return (
    <List dense={false} sx={listStyle} key={props.listIndex}>
      {props.activityRecords.map((record, index) => {
        const isExpanded = expandedComments[index];
        const comment = record.Comment || "";
        const shouldShowMore = comment.length > 140;

        return (
          <ListItem key={index} sx={listItemStyle}>
            <ListItemText
              primaryTypographyProps={{ component: "span", variant: "body2" }}
              primary={
                <div style={{ paddingBottom: "7px" }}>
                  <div>{getActivityDescription(record)}</div>
                  {record.Comment != null && record.Activity != ActivityType.ACTIVATE && (
                    <>
                      <div>Comment:</div>
                      <div key={index} data-testid="comment">
                        {(isExpanded
                          ? comment
                          : `${comment.substring(0, 140)}${shouldShowMore ? "..." : ""}`
                        )
                          .replace(/\r\n|\r/g, "\n")
                          .split("\n")
                          .map((line, idx) => (
                            <React.Fragment key={idx}>
                              {line}
                              <br />
                            </React.Fragment>
                          ))}
                        {shouldShowMore && (
                          <span
                            role="button"
                            style={{
                              color: "divider",
                              cursor: "pointer",
                              textDecorationLine: "underline",
                              marginLeft: "4px",
                              fontWeight: "600",
                            }}
                            tabIndex={0}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                            onClick={() => toggleExpand(index)}
                          >
                            {isExpanded ? "Show less" : "Show more"}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              }
              secondaryTypographyProps={{
                variant: "caption",
                color: "info.main",
                fontWeight: "600",
              }}
              secondary={computeOccurredDateDetails(record.ActivityDatetime)}
              sx={{
                display: isExpanded ? "block" : "-webkit-box",
                WebkitBoxOrient: isExpanded ? "initial" : "vertical",
                textOverflow: isExpanded ? "initial" : "none",
                overflow: isExpanded ? "visible" : "hidden",
                wordWrap: "break-word",
              }}
            ></ListItemText>
          </ListItem>
        );
      })}
    </List>
  );
};
